import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CalendarDays, CalendarRange, Clock3, ExternalLink, LoaderCircle, MapPin, Ticket, Users } from 'lucide-react';
import EventCard from '../components/EventCard';
import usePublicEvents, { formatPublicEventDate, formatPublicEventTime } from '../hooks/usePublicEvents';

const EmptyEventsState = ({ compact = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className={`overflow-hidden border border-white/10 bg-white/[0.04] ${compact ? 'rounded-[1.75rem]' : 'rounded-[2rem]'}`}
  >
    <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
      <div className="relative overflow-hidden bg-gradient-to-br from-[#04144c] via-[#082b57] to-[#17E6B3]/25 p-8 md:p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(23,230,179,0.2),transparent_32%)]" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-[#9CF7DF] backdrop-blur-sm">
            <CalendarRange size={16} />
            Agenda em atualizacao
          </span>
          <h2 className="mt-6 max-w-md text-3xl font-bold text-white md:text-4xl">
            Nenhum evento publicado no momento.
          </h2>
          <p className="mt-4 max-w-lg text-base leading-7 text-[#D9E4FF]">
            Assim que uma nova programação estiver disponível, ela aparecerá aqui com data, local e acesso para inscrição.
          </p>

          <div className="mt-8 grid gap-3 text-sm text-[#EAF1FF] sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <p className="font-semibold text-white">Sempre atualizado</p>
              <p className="mt-1 text-[#BFD2FF]">As programações são atualizadas conforme novos eventos são divulgados.</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4">
              <p className="font-semibold text-white">Acompanhe os canais</p>
              <p className="mt-1 text-[#BFD2FF]">Voce tambem pode falar com a igreja ou acompanhar a agenda regular da casa.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center bg-white px-6 py-8 text-[#030B2E] md:px-10 md:py-10">
        <h3 className="text-2xl font-bold">Enquanto isso, voce pode:</h3>
        <div className="mt-6 space-y-4">
          <div className="rounded-2xl border border-slate-200 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Agenda da igreja</p>
            <p className="mt-2 font-semibold">Acompanhar os cultos e programacoes fixas da semana.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 px-5 py-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Contato direto</p>
            <p className="mt-2 font-semibold">Receber informacoes sobre as proximas inscricoes e eventos especiais.</p>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/contato"
            className="inline-flex items-center justify-center rounded-2xl bg-[#17E6B3] px-6 py-4 font-semibold text-[#030B2E] transition-colors hover:bg-[#0ed3a3]"
          >
            Falar com a igreja
          </Link>
          <Link
            to="/agenda"
            className="inline-flex items-center justify-center rounded-2xl border border-slate-300 px-6 py-4 font-semibold text-slate-700 transition-colors hover:border-[#17E6B3] hover:text-[#030B2E]"
          >
            Ver agenda fixa
          </Link>
        </div>
      </div>
    </div>
  </motion.div>
);

const Eventos = () => {
  const { events, loading, error } = usePublicEvents();

  const featuredEvent = useMemo(
    () => events.find((event) => event.featured) || events[0],
    [events]
  );

  const upcomingEvents = useMemo(
    () => events.filter((event) => event.id !== featuredEvent?.id),
    [events, featuredEvent]
  );

  return (
    <div className="bg-[#030B2E] pt-28 text-white md:pt-24">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#030B2E] via-[#04144c] to-[#17E6B3]/20 pb-12 pt-10 md:pb-18 md:pt-12 lg:pb-20 lg:pt-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(23,230,179,0.18),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.08),transparent_24%)]" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-[#9CF7DF] backdrop-blur-sm">
              <Ticket size={16} />
              Eventos com inscrição
            </span>
            <h1 className="mt-5 text-3xl font-bold leading-tight md:mt-6 md:text-5xl lg:text-6xl">
              Eventos MEVAM Brasília
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-7 text-[#D9E4FF] md:mt-6 md:text-xl">
              Confira os eventos com inscrição aberta. Veja datas, horários, locais e inscreva-se diretamente.
            </p>

            <div className="mt-8 hidden gap-3 sm:grid-cols-3 md:grid md:gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Programação</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Veja os eventos disponíveis com datas, horários e locais.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Inscrição</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Acesse a página do evento e faça sua inscrição de forma rápida e simples.</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4 backdrop-blur-sm">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9CF7DF]">Novidades</p>
                <p className="mt-2 text-sm text-[#D9E4FF]">Fique por dentro das próximas programações e eventos especiais.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pt-8 pb-10 md:pt-10 lg:pt-12">
        <div className="container-custom">
          {error && (
            <div className="mb-8 rounded-2xl border border-amber-400/30 bg-amber-300/10 px-5 py-4 text-sm text-amber-100">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center rounded-3xl border border-white/10 bg-white/5">
              <div className="flex items-center gap-3 text-[#D9E4FF]">
                <LoaderCircle className="animate-spin text-[#17E6B3]" size={22} />
                Carregando eventos...
              </div>
            </div>
          ) : (
            featuredEvent ? (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04] shadow-[0_24px_80px_rgba(0,0,0,0.25)]"
              >
                <div className="grid gap-0 lg:grid-cols-[1.2fr_0.8fr]">
                  <div className="relative min-h-[320px] overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: `linear-gradient(135deg, rgba(3,11,46,0.08), rgba(3,11,46,0.68)), url(${featuredEvent.imageUrl || 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=1200&q=80'})`
                      }}
                    />
                    <div className="absolute left-6 top-6 rounded-2xl border border-white/15 bg-white/95 px-4 py-3 text-center text-[#030B2E] shadow-[0_8px_24px_rgba(0,0,0,0.18)] md:left-8 md:top-8">
                      <p className="text-3xl font-bold leading-none">
                        {new Intl.DateTimeFormat('pt-BR', { day: '2-digit' }).format(new Date(featuredEvent.startDate))}
                      </p>
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                        {new Intl.DateTimeFormat('pt-BR', { month: 'short' }).format(new Date(featuredEvent.startDate)).replace('.', '').toUpperCase()}
                      </p>
                    </div>
                    <div className="relative flex h-full flex-col justify-end p-6 md:p-8 lg:p-10">
                      <span className="mb-4 inline-flex w-fit rounded-full bg-[#17E6B3] px-4 py-2 text-sm font-bold text-[#030B2E]">
                        Evento em destaque
                      </span>
                      <p className="text-xs uppercase tracking-[0.32em] text-[#9CF7DF] md:text-sm">
                        {featuredEvent.subtitle}
                      </p>
                      <h2 className="mt-3 max-w-xl text-3xl font-bold leading-tight md:text-4xl lg:text-5xl">
                        {featuredEvent.title}
                      </h2>
                      {featuredEvent.category && (
                        <p className="mt-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#7fe7cc]">
                          {featuredEvent.category}
                        </p>
                      )}
                      <p className="mt-4 max-w-lg text-sm leading-7 text-[#EAF1FF] md:text-lg">
                        {featuredEvent.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between bg-white px-6 py-8 text-[#030B2E] md:px-8">
                    <div>
                      <h3 className="text-xl font-bold">Detalhes e inscrição</h3>
                      <p className="mt-2 text-sm text-slate-500">
                        Confira as informações do evento e garanta sua vaga.
                      </p>

                      <div className="mt-8 space-y-4">
                        <div className="flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                          <CalendarDays className="mt-0.5 text-[#17E6B3]" size={18} />
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Data</p>
                            <p className="font-semibold">{formatPublicEventDate(featuredEvent.startDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                          <Clock3 className="mt-0.5 text-[#17E6B3]" size={18} />
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Horário</p>
                            <p className="font-semibold">{formatPublicEventTime(featuredEvent.startDate)}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                          <MapPin className="mt-0.5 text-[#17E6B3]" size={18} />
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Local</p>
                            <p className="font-semibold">{featuredEvent.location}</p>
                            <p className="text-sm text-slate-500">{featuredEvent.address}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 rounded-2xl border border-slate-200 px-4 py-3">
                          <Users className="mt-0.5 text-[#17E6B3]" size={18} />
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Acesso</p>
                            <p className="font-semibold">{featuredEvent.capacity}</p>
                            <p className="text-sm text-slate-500">Status: {featuredEvent.status}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-8 space-y-3">
                      <a
                        href={featuredEvent.eventUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#17E6B3] px-6 py-4 font-semibold text-[#030B2E] transition-colors hover:bg-[#0ed3a3]"
                      >
                        Entrar no evento para se inscrever
                        <ExternalLink size={18} />
                      </a>

                      <p className="text-xs leading-6 text-slate-500">
                        Acompanhe esta página para ficar por dentro das próximas programações.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <EmptyEventsState />
            )
          )}
        </div>
      </section>

      <section className="section-padding pt-8">
        <div className="container-custom">
          <div className="mb-10 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-3xl font-bold">Próximos eventos</h2>
              <p className="mt-2 text-[#B8C4E3]">
                Confira outros eventos com inscrição aberta.
              </p>
            </div>
          </div>

          {upcomingEvents.length ? (
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.08 }}
                  viewport={{ once: true }}
                >
                  <EventCard event={event} />
                </motion.div>
              ))}
            </div>
          ) : (
            featuredEvent ? (
              <div className="rounded-[1.75rem] border border-white/10 bg-gradient-to-r from-white/[0.04] to-white/[0.02] px-6 py-12 text-center text-[#D9E4FF]">
                <div className="mx-auto max-w-2xl">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-[#9CF7DF]">
                    <CalendarRange size={16} />
                    Agenda atual
                  </span>
                  <h3 className="mt-5 text-2xl font-bold text-white">No momento, este é o único evento com inscrição aberta.</h3>
                  <p className="mt-3 text-base leading-7 text-[#B8C4E3]">
                    Fique atento — novas programações serão divulgadas em breve.
                  </p>
                </div>
              </div>
            ) : (
              <EmptyEventsState compact />
            )
          )}
        </div>
      </section>
    </div>
  );
};

export default Eventos;